import { AHPageIndicator, AHText, Box, ScaleFactorProvider } from "arkh";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { ActivityIndicator, useWindowDimensions } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import { useNavigationHeaderHeight } from "../../../TopLevelNavigators/AHNavigationHeaderContainer";
import { UserLoggedInNavigatorScreenProps } from "../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import Gallery, { defaultRenderImage } from "../VerticalGallery";

import { useDebouncedCallback } from "use-debounce";
import { FormattedMessage } from "react-intl";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";

import { LazyQueryNetworkBoundrary } from "../../../components/mobile/QueryNetworkBoundrary";
import {
  FileFocusQuery,
  FileStatus,
} from "./__generated__/FileFocusQuery.graphql";
import { notEmpty } from "../../../utils";
import { useSubscriptionWithSkip } from "widgets/src/utils/useSubscriptionWithSkip";
import { FileFocusSubscription } from "./__generated__/FileFocusSubscription.graphql";
import { FileFocusNewMutation } from "./__generated__/FileFocusNewMutation.graphql";

export function FileFocus(
  props: UserLoggedInNavigatorScreenProps<"FileFocus">
) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey) => <InnerFileFocus {...props} fetchKey={fetchKey} />}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerFileFocus(
  props: UserLoggedInNavigatorScreenProps<"FileFocus"> & { fetchKey: string }
) {
  const [status, setStatus] = useState<FileStatus | null>(null);
  const data = useLazyLoadQuery<FileFocusQuery>(
    graphql`
      query FileFocusQuery($fileId: ID!) {
        store {
          id
          file(id: $fileId) {
            id
            status
            name
            pages {
              id
              image {
                id
                ...Image_image
              }
            }
          }
        }
      }
    `,
    {
      fileId: props.route.params.fileId,
    },
    {
      fetchPolicy: "store-and-network",
    }
  );
  useEffect(() => {
    if (data.store.file) {
      setStatus(data.store.file.status);
    }
  }, [data.store.file, data.store.file?.status]);

  const width = useWindowDimensions().width;
  const height =
    useWindowDimensions().height -
    useNavigationHeaderHeight() -
    useSafeAreaInsets().top;

  const [focusedIndex, setFocusedIndex] = useState<number>(0);

  const [fileNewUpdate] = useMutation<FileFocusNewMutation>(graphql`
    mutation FileFocusNewMutation(
      $storecastId: ID!
      $subject: StorecastFocusSubjectNewInput!
    ) {
      dashboard(dashboardId: $storecastId) {
        setNewFocus(subject: $subject) {
          subject {
            nodeId
          }
        }
      }
    }
  `);

  const fileFocusUpdate = useCallback(
    (input: {
      pageId: string;
      transformY?: number;
      transformX?: number;
      scale?: number;
    }) => {
      fileNewUpdate({
        variables: {
          storecastId: props.route.params.storecastId,
          subject: {
            nodeId: props.route.params.fileId,
            detailItemId: input.pageId,
            transformY: input.transformY,
            transformX: input.transformX,
            scale: input.scale,
          },
        },
      });
    },
    [fileNewUpdate, props.route.params.fileId, props.route.params.storecastId]
  );

  const name = data?.store.file?.name;
  useLayoutEffect(() => {
    if (name) {
      props.navigation.setOptions({ title: name });
    }
  }, [name, props.navigation]);

  useSubscriptionWithSkip<FileFocusSubscription>(
    useMemo(
      () => ({
        variables: {
          fileId: props.route.params.fileId,
        },
        subscription: graphql`
          subscription FileFocusSubscription($fileId: ID!) {
            filePendingStatusChange {
              listFiles(fileIds: [$fileId]) {
                id
                status
                name
                pages {
                  id
                  image {
                    id
                    ...Image_image
                  }
                }
              }
            }
          }
        `,
      }),
      [props.route.params.fileId]
    ),
    {
      skip: status === "READY",
    }
  );

  // useEffect(() => {
  //   if (status && status !== FileStatus.READY) {
  //     startPolling(2000);
  //   } else {
  //     stopPolling();
  //   }
  //   return () => {
  //     stopPolling();
  //   };
  // }, [status, startPolling, stopPolling]);

  const pages = useMemo(
    () => data?.store.file?.pages ?? [],
    [data?.store.file?.pages]
  );

  useEffect(() => {
    if (pages[focusedIndex]) {
      fileFocusUpdate({
        pageId: pages[focusedIndex].id,
      });
    }
  }, [fileFocusUpdate, focusedIndex, pages]);

  const onScaleChanged = useCallback(
    (params: { scale: number; translateX: number; translateY: number }) => {
      if (pages[focusedIndex]) {
        fileFocusUpdate({
          pageId: pages[focusedIndex].id,
          scale: params.scale,
          transformX: params.translateX,
          transformY: params.translateY,
        });
      }
    },
    [fileFocusUpdate, focusedIndex, pages]
  );

  const onScaleOrTranslationChnage = useDebouncedCallback(onScaleChanged, 300);

  const d = useMemo(
    () =>
      pages
        .map((page) => [page.id, page.image] as const)
        .filter(notEmpty)
        .map(([id, image]) => ({
          type: "remote" as const,
          image: image!,
          id,
        })) ?? [],
    [pages]
  );
  if (status !== "READY") {
    return <FileNotReady />;
  }

  return (
    <ScaleFactorProvider value={1}>
      <Gallery
        data={d}
        renderItem={defaultRenderImage}
        containerDimensions={{ height, width }}
        onIndexChange={setFocusedIndex}
        onScaleOrTranslationChange={onScaleOrTranslationChnage}
      />
      {pages.length > 1 ? (
        <Box
          style={{
            position: "absolute",
            right: 0,
            top: 0,
            width: 30,
            bottom: 0,
            height: "100%",
            justifyContent: "center",
          }}
        >
          <AHPageIndicator
            direction="vertical"
            currentIndex={focusedIndex}
            numberOfItems={pages.length}
          />
        </Box>
      ) : null}
    </ScaleFactorProvider>
  );
}

const FileNotReady_ = function () {
  return (
    <Box space="xlarge">
      <AHText spaceVertical="large" type="title-big">
        <FormattedMessage
          defaultMessage="The file is being processed"
          description="FileNotReady title"
        />
        {/* Filen processas */}
      </AHText>
      <AHText spaceBottom="xxlarge" type="subtitle">
        <FormattedMessage
          defaultMessage="In order for the file to be displayed, we need to search through it. It can take between 1-2 minutes from when it was uploaded."
          description="FileNotReady description"
        />
      </AHText>
      <Box spaceTop="xxlarge">
        <ActivityIndicator size="large" />
      </Box>
    </Box>
  );
};

export const FileNotReady = React.memo(FileNotReady_);
